import { useEffect, useState } from 'react';
import { subscribeSiteSettings } from '@/data/settingsRepo';
import { makeDefaultSiteSettings, type SiteSettingsType } from '@/types/types_settings';

/**
 * Subscribes to the site settings in real time.
 *
 * Live rather than read once, so flipping a feature flag reaches everyone who
 * already has the site open — which is the whole point of a switch a person can
 * reach. A concierge does not have to reload to stop seeing a section we have
 * just withdrawn.
 *
 * Starts from the defaults rather than a null, so no caller has to handle "not
 * loaded yet" as a third state: before the document arrives the site behaves as
 * it does with no settings saved at all. `loading` is there for the admin screen,
 * which does need to tell "off" from "not yet known" before it draws a switch.
 */
export function useSiteSettings() {
  const [settings, setSettings] = useState<SiteSettingsType>(makeDefaultSiteSettings);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    return subscribeSiteSettings(
      (next) => {
        setSettings(next);
        setLoading(false);
      },
      (e) => {
        // Never fatal: the defaults already loaded are a working site, and a
        // settings read that fails must not take the catalogue down with it.
        console.error('Site settings subscription failed', e);
        setLoading(false);
      },
    );
  }, []);

  return { settings, loading };
}
